// frontend/src/admin/PaymentManagement.js
import React, { useState, useEffect } from 'react';
import {
  FaChartLine, FaEye, FaBarcode, FaSyncAlt, FaArrowUp, FaArrowDown,
  FaMapMarkerAlt, FaPlus, FaTimes, FaTrash, FaSave
} from 'react-icons/fa';
import axiosInstance from '../axios';
import { useAuth } from '../context/AuthContext';
import '../styles/admin.css';
import '../styles/custom.css';

const PaymentManagement = () => {
  const { user, loading: authLoading } = useAuth();

  // ==================== STATES ====================
  const [activeTab, setActiveTab] = useState('revenue');
  const [containers, setContainers] = useState([]);
  const [paymentLocations, setPaymentLocations] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    korea_revenue: { count: 0, cargo_total: 0, storage_total: 0, total: 0 },
    mongolia_revenue: { count: 0, cargo_total: 0, storage_total: 0, total: 0 },
    total: { cargo_count: 0, registered_count: 0, delivered_count: 0, distributed_count: 0, cargo_price: 0, storage_fee: 0, grand_total: 0 }
  });
  const [expenseTotal, setExpenseTotal] = useState(0);

  // Валютын ханш
  const [exchangeRates, setExchangeRates] = useState({
    KRW: { buy: null, sell: null },
    USD: { buy: null, sell: null },
    CNY: { buy: null, sell: null }
  });
  const [activeCurrency, setActiveCurrency] = useState('KRW');
  const [ratesLoading, setRatesLoading] = useState(true);

  // Filter
  const [filters, setFilters] = useState({
    container_id: 'all',
    cargo_status: 'all',
    payment_location: 'all',
    start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  // Modals
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailCargo, setDetailCargo] = useState(null);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showExpenseDetailModal, setShowExpenseDetailModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  
  // Expense form
  const [expenseForm, setExpenseForm] = useState({
    container_id: '',
    amount: '',
    currency_code: 'KRW',
    payment_method: 'cash',
    description: ''
  });
  const [expenseLoading, setExpenseLoading] = useState(false);

  // ==================== PERMISSIONS ====================
  const hasPermission = (permission) => {
    if (!user || !Array.isArray(user.perms)) return false;
    return user.perms.includes(permission);
  };

  const canView = hasPermission('payment:view');
  const canEdit = hasPermission('payment:edit');
  const canDelete = hasPermission('payment:delete');

  // ==================== FETCH FUNCTIONS ====================
  const fetchContainers = async () => {
    try {
      const response = await axiosInstance.get('/api/report/containers');
      if (response.data.success) {
        setContainers(response.data.data || []);
      }
    } catch (error) {
      console.error('Containers fetch error:', error);
    }
  };

  const fetchPaymentLocations = async () => {
    try {
      const response = await axiosInstance.get('/api/payment-locations');
      if (response.data.success) {
        setPaymentLocations(response.data.data || []);
      }
    } catch (error) {
      console.error('Payment locations fetch error:', error);
    }
  };

  const fetchCargos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.container_id !== 'all') params.append('container_id', filters.container_id);
      if (filters.cargo_status !== 'all') params.append('cargo_status', filters.cargo_status);
      if (filters.payment_location !== 'all') params.append('payment_location', filters.payment_location);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axiosInstance.get(`/api/report/cargos?${params}`);
      if (response.data.success) {
        setCargos(response.data.data || []);
      }
    } catch (error) {
      console.error('Cargos fetch error:', error);
      setCargos([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.container_id !== 'all') params.append('container_id', filters.container_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axiosInstance.get(`/api/report/revenue-stats?${params}`);
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Stats fetch error:', error);
    }
  };

  const fetchExpenses = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.container_id !== 'all') params.append('container_id', filters.container_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axiosInstance.get(`/api/report/expenses?${params}`);
      if (response.data.success) {
        setExpenses(response.data.data || []);
        setExpenseTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error('Expenses fetch error:', error);
    }
  };

  const fetchExchangeRates = async () => {
    setRatesLoading(true);
    try {
      const response = await axiosInstance.get('/api/exchange-rates');
      if (response.data.success && response.data.rates) {
        setExchangeRates(response.data.rates);
      }
    } catch (error) {
      console.error('Exchange rates fetch error:', error);
    } finally {
      setRatesLoading(false);
    }
  };

  // ==================== HANDLERS ====================
  const handleAddExpense = async () => {
    if (!expenseForm.container_id || !expenseForm.amount) {
      alert('Чингэлэг болон дүн оруулна уу');
      return;
    }
    setExpenseLoading(true);
    try {
      const response = await axiosInstance.post('/api/report/expenses', expenseForm);
      if (response.data.success) {
        alert('Зарлага амжилттай бүртгэгдлээ');
        setShowExpenseModal(false);
        setExpenseForm({ container_id: '', amount: '', currency_code: 'KRW', payment_method: 'cash', description: '' });
        fetchExpenses();
        fetchStats();
      }
    } catch (error) {
      alert('Алдаа: ' + (error.response?.data?.msg || error.message));
    } finally {
      setExpenseLoading(false);
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Энэ зарлагыг устгах уу?')) return;
    try {
      const response = await axiosInstance.delete(`/api/report/expenses/${id}`);
      if (response.data.success) {
        fetchExpenses();
        fetchStats();
      }
    } catch (error) {
      alert('Алдаа гарлаа');
    }
  };

  // ==================== EFFECTS ====================
  useEffect(() => {
    if (!authLoading && canView) {
      fetchContainers();
      fetchPaymentLocations();
      fetchExchangeRates();
    }
  }, [authLoading]);

  useEffect(() => {
    if (!authLoading && canView) {
      fetchCargos();
      fetchStats();
      fetchExpenses();
    }
  }, [filters, authLoading]);

  // ==================== HELPER FUNCTIONS ====================
  const getStatusBadge = (status) => {
    const statusMap = {
      'registered': { class: 'badge-primary', text: 'Бүртгэгдсэн' },
      'delivered': { class: 'badge-info', text: 'Хүлээн авсан' },
      'distributed': { class: 'badge-success', text: 'Тараагдсан' }
    };
    const info = statusMap[status] || { class: 'badge-secondary', text: status };
    return <span className={`badge ${info.class}`}>{info.text}</span>;
  };

  const getContainerStatusText = (status) => {
    const map = { 'open': 'Нээлттэй', 'closed': 'Хаалттай', 'settlement': 'Тооцоо' };
    return map[status] || status;
  };

  const formatCurrency = (amount, currency = 'KRW') => {
    const num = parseFloat(amount) || 0;
    const symbol = currency === 'KRW' ? '₩' : currency === 'MNT' ? '₮' : currency;
    return `${num.toLocaleString()}${symbol}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('mn-MN', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('mn-MN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  // ==================== RENDER ====================
  if (authLoading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="loading-spinner"></div></div>;
  }

  if (!canView) {
    return (
      <div className="p-4 md:p-6">
        <h1 className="text-2xl font-bold mb-4">Тайлан тооцоо</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 font-semibold">Эрх хүрэхгүй байна</p>
        </div>
      </div>
    );
  }

  const totalRevenue = (stats.korea_revenue?.total || 0) + (stats.mongolia_revenue?.total || 0);

  return (
    <div className="payment-management min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 flex items-center">
          <FaChartLine className="mr-3 text-blue-600" />
          Тайлан тооцоо
        </h1>
        <p className="text-sm text-gray-600">Орлого, зарлагын тайлан</p>
      </div>

      {/* Валютын ханш */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FaSyncAlt className={`text-blue-500 ${ratesLoading ? 'animate-spin' : ''}`} />
            <span className="text-sm font-medium text-gray-700">Валютын ханш:</span>
          </div>
          {['KRW', 'USD', 'CNY'].map((currency) => (
            <button
              key={currency}
              onClick={() => setActiveCurrency(currency)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeCurrency === currency ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {currency}
              {exchangeRates[currency]?.sell && <span className="ml-2">{parseFloat(exchangeRates[currency].sell).toLocaleString()}₮</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Статистик картууд */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <FaMapMarkerAlt className="text-2xl opacity-80" />
            <span className="text-xs bg-white/20 px-2 py-1 rounded">Солонгос</span>
          </div>
          <div className="text-2xl font-bold mb-1">{formatCurrency(stats.korea_revenue?.total || 0)}</div>
          <div className="text-xs opacity-80">{stats.korea_revenue?.count || 0} ачаа</div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <FaMapMarkerAlt className="text-2xl opacity-80" />
            <span className="text-xs bg-white/20 px-2 py-1 rounded">Монгол</span>
          </div>
          <div className="text-2xl font-bold mb-1">{formatCurrency(stats.mongolia_revenue?.total || 0)}</div>
          <div className="text-xs opacity-80">Ачаа: {formatCurrency(stats.mongolia_revenue?.cargo_total || 0)} | Хадгалалт: {formatCurrency(stats.mongolia_revenue?.storage_total || 0)}</div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <FaArrowUp className="text-2xl opacity-80" />
            <span className="text-xs bg-white/20 px-2 py-1 rounded">Нийт орлого</span>
          </div>
          <div className="text-2xl font-bold mb-1">{formatCurrency(totalRevenue)}</div>
          <div className="text-sm opacity-80">Солонгос + Монгол</div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-5 text-white shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <FaArrowDown className="text-2xl opacity-80" />
            <span className="text-xs bg-white/20 px-2 py-1 rounded">Зарлага</span>
          </div>
          <div className="text-2xl font-bold mb-1">{formatCurrency(expenseTotal)}</div>
          <div className="text-sm opacity-80">{expenses.length} бүртгэл</div>
        </div>
      </div>

      {/* Filter хэсэг */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <select className="w-56 px-3 py-2 border border-gray-200 rounded-lg text-sm" value={filters.container_id} onChange={(e) => setFilters({ ...filters, container_id: e.target.value })}>
            <option value="all">Бүх чингэлэг ({containers.length})</option>
            {containers.map((c) => <option key={c.id} value={c.id}>{c.name || c.container_code} ({getContainerStatusText(c.status)})</option>)}
          </select>

          <select className="w-44 px-3 py-2 border border-gray-200 rounded-lg text-sm" value={filters.cargo_status} onChange={(e) => setFilters({ ...filters, cargo_status: e.target.value })}>
            <option value="all">Ачааны төлөв</option>
            <option value="registered">Бүртгэгдсэн</option>
            <option value="delivered">Хүлээн авсан</option>
            <option value="distributed">Тараагдсан</option>
          </select>

          <select className="w-48 px-3 py-2 border border-gray-200 rounded-lg text-sm" value={filters.payment_location} onChange={(e) => setFilters({ ...filters, payment_location: e.target.value })}>
            <option value="all">Төлбөрийн байршил</option>
            {paymentLocations.map((loc) => <option key={loc.id} value={loc.location_code}>{loc.location_name}</option>)}
          </select>

          <div className="h-6 w-px bg-gray-300 mx-1"></div>

          <input type="date" className="px-3 py-2 border border-gray-200 rounded-lg text-sm" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
          <span className="text-gray-400">→</span>
          <input type="date" className="px-3 py-2 border border-gray-200 rounded-lg text-sm" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
        </div>
      </div>

      {/* Табууд */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex border-b">
          <button onClick={() => setActiveTab('revenue')} className={`flex-1 py-4 text-center font-medium ${activeTab === 'revenue' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500' : 'text-gray-600 hover:bg-gray-50'}`}>
            <FaArrowUp className="inline mr-2" />Орлого ({cargos.length})
          </button>
          <button onClick={() => setActiveTab('expense')} className={`flex-1 py-4 text-center font-medium ${activeTab === 'expense' ? 'bg-red-50 text-red-600 border-b-2 border-red-500' : 'text-gray-600 hover:bg-gray-50'}`}>
            <FaArrowDown className="inline mr-2" />Зарлага ({expenses.length})
          </button>
        </div>

        {/* Орлого таб */}
        {activeTab === 'revenue' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Ачааны код</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Илгээгч</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Хүлээн авагч</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Чингэлэг</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Байршил</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Ачааны үнэ</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Хадгалалт</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Нийт</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Төлөв</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Огноо</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Үйлдэл</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan="11" className="text-center py-8"><div className="loading-spinner mx-auto"></div></td></tr>
                ) : cargos.length === 0 ? (
                  <tr><td colSpan="11" className="text-center py-8 text-gray-500">Ачаа олдсонгүй</td></tr>
                ) : (
                  cargos.map((cargo) => (
                    <tr key={cargo.batch_number} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FaBarcode className="text-blue-500" />
                          <span className="font-medium">{cargo.batch_number || cargo.cargo_code}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium">{cargo.sender_name}</div>
                        <div className="text-gray-500 text-xs">{cargo.sender_phone || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium">{cargo.receiver_name}</div>
                        <div className="text-gray-500 text-xs">{cargo.receiver_phone || '-'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="badge badge-primary">{cargo.container_name || cargo.container_code}</span>
                        <span className="text-xs text-gray-500 ml-1">({getContainerStatusText(cargo.container_status)})</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${cargo.payment_location_code === 'KR' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {cargo.payment_location_name || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(cargo.cargo_price, cargo.currency_code)}</td>
                      <td className="px-4 py-3 text-right">
                        {parseFloat(cargo.storage_fee) > 0 ? (
                          <span className="text-orange-600">{formatCurrency(cargo.storage_fee, cargo.currency_code)}</span>
                        ) : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-green-600">{formatCurrency(cargo.total_amount, cargo.currency_code)}</td>
                      <td className="px-4 py-3 text-center">{getStatusBadge(cargo.status)}</td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600">{formatDate(cargo.distributed_date || cargo.registered_date)}</td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => { setDetailCargo(cargo); setShowDetailModal(true); }} className="text-blue-600 hover:text-blue-800"><FaEye /></button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {cargos.length > 0 && (
                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                  <tr>
                    <td colSpan="5" className="px-4 py-3 text-right font-semibold text-gray-700">Дүн ({cargos.length} ачаа):</td>
                    <td className="px-4 py-3 text-right font-bold text-gray-800">{formatCurrency(cargos.reduce((sum, c) => sum + parseFloat(c.cargo_price || 0), 0))}</td>
                    <td className="px-4 py-3 text-right font-bold text-orange-600">{formatCurrency(cargos.reduce((sum, c) => sum + parseFloat(c.storage_fee || 0), 0))}</td>
                    <td className="px-4 py-3 text-right font-bold text-green-600">{formatCurrency(cargos.reduce((sum, c) => sum + parseFloat(c.total_amount || 0), 0))}</td>
                    <td colSpan="3"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {/* Зарлага таб */}
        {activeTab === 'expense' && (
          <div>
            {canEdit && (
              <div className="p-4 border-b">
                <button onClick={() => setShowExpenseModal(true)} className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
                  <FaPlus />Зарлага бүртгэх
                </button>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Огноо</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Чингэлэг</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Тайлбар</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Төлбөрийн хэлбэр</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Дүн</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Бүртгэсэн</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Үйлдэл</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {expenses.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-8 text-gray-500">Зарлага олдсонгүй</td></tr>
                  ) : (
                    expenses.map((expense) => (
                      <tr key={expense.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{formatDate(expense.payment_date)}</td>
                        <td className="px-4 py-3"><span className="badge badge-primary">{expense.container_name || expense.container_code || '-'}</span></td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">{expense.notes || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded ${expense.payment_method === 'cash' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                            {expense.payment_method === 'cash' ? 'Бэлэн' : 'Шилжүүлэг'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-red-600">{formatCurrency(expense.total_amount, expense.currency_code)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{expense.recorded_by_name || '-'}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => { setSelectedExpense(expense); setShowExpenseDetailModal(true); }} className="text-blue-600 hover:text-blue-800"><FaEye /></button>
                            {canDelete && <button onClick={() => handleDeleteExpense(expense.id)} className="text-red-600 hover:text-red-800"><FaTrash /></button>}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {expenses.length > 0 && (
                  <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                    <tr>
                      <td colSpan="4" className="px-4 py-3 text-right font-semibold text-gray-700">Нийт зарлага:</td>
                      <td className="px-4 py-3 text-right font-bold text-red-600">{formatCurrency(expenseTotal)}</td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Ачааны дэлгэрэнгүй Modal */}
      {showDetailModal && detailCargo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">Ачааны дэлгэрэнгүй</h3>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl"><FaTimes /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm text-gray-500">Ачааны код</label><p className="font-semibold">{detailCargo.batch_number}</p></div>
                <div><label className="text-sm text-gray-500">Төлөв</label><p>{getStatusBadge(detailCargo.status)}</p></div>
                <div><label className="text-sm text-gray-500">Илгээгч</label><p className="font-medium">{detailCargo.sender_name}</p><p className="text-sm text-gray-500">{detailCargo.sender_phone || '-'}</p></div>
                <div><label className="text-sm text-gray-500">Хүлээн авагч</label><p className="font-medium">{detailCargo.receiver_name}</p><p className="text-sm text-gray-500">{detailCargo.receiver_phone || '-'}</p></div>
                <div><label className="text-sm text-gray-500">Чингэлэг</label><p className="font-medium">{detailCargo.container_name}</p></div>
                <div><label className="text-sm text-gray-500">Төлбөрийн байршил</label><p className="font-medium">{detailCargo.payment_location_name || '-'}</p></div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Төлбөрийн мэдээлэл</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between"><span className="text-gray-600">Ачааны үнэ:</span><span className="font-medium">{formatCurrency(detailCargo.cargo_price)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Хадгалалтын хөлс ({detailCargo.storage_days || 0} өдөр):</span><span className="font-medium text-orange-600">{formatCurrency(detailCargo.storage_fee)}</span></div>
                  <div className="flex justify-between border-t pt-2"><span className="font-semibold">Нийт дүн:</span><span className="font-bold text-green-600 text-lg">{formatCurrency(detailCargo.total_amount)}</span></div>
                </div>
              </div>
              <div className="border-t pt-4 grid grid-cols-2 gap-4 text-sm">
                <div><label className="text-gray-500">Бүртгэсэн огноо</label><p>{formatDateTime(detailCargo.registered_date)}</p></div>
                <div><label className="text-gray-500">Тараагдсан огноо</label><p>{formatDateTime(detailCargo.distributed_date)}</p></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Зарлага бүртгэх Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">Зарлага бүртгэх</h3>
              <button onClick={() => setShowExpenseModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl"><FaTimes /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Чингэлэг <span className="text-red-500">*</span></label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg" value={expenseForm.container_id} onChange={(e) => setExpenseForm({ ...expenseForm, container_id: e.target.value })}>
                  <option value="">Сонгоно уу</option>
                  {containers.map((c) => <option key={c.id} value={c.id}>{c.name || c.container_code} ({getContainerStatusText(c.status)})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Дүн <span className="text-red-500">*</span></label>
                <div className="flex gap-2">
                  <input type="number" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg" placeholder="0" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} />
                  <select className="w-24 px-3 py-2 border border-gray-300 rounded-lg" value={expenseForm.currency_code} onChange={(e) => setExpenseForm({ ...expenseForm, currency_code: e.target.value })}>
                    <option value="KRW">₩</option>
                    <option value="MNT">₮</option>
                    <option value="USD">$</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Төлбөрийн хэлбэр</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="payment_method" value="cash" checked={expenseForm.payment_method === 'cash'} onChange={(e) => setExpenseForm({ ...expenseForm, payment_method: e.target.value })} />
                    <span>Бэлэн</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="payment_method" value="transfer" checked={expenseForm.payment_method === 'transfer'} onChange={(e) => setExpenseForm({ ...expenseForm, payment_method: e.target.value })} />
                    <span>Шилжүүлэг</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Тайлбар</label>
                <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows="3" placeholder="Зарлагын тайлбар..." value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} />
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => setShowExpenseModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800">Цуцлах</button>
              <button onClick={handleAddExpense} disabled={expenseLoading} className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50">
                {expenseLoading ? <div className="loading-spinner w-4 h-4"></div> : <FaSave />}Хадгалах
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Зарлагын дэлгэрэнгүй Modal */}
      {showExpenseDetailModal && selectedExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">Зарлагын дэлгэрэнгүй</h3>
              <button onClick={() => setShowExpenseDetailModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl"><FaTimes /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm text-gray-500">Чингэлэг</label><p className="font-semibold">{selectedExpense.container_name || '-'}</p></div>
                <div><label className="text-sm text-gray-500">Огноо</label><p className="font-medium">{formatDateTime(selectedExpense.payment_date)}</p></div>
                <div><label className="text-sm text-gray-500">Дүн</label><p className="font-bold text-red-600 text-lg">{formatCurrency(selectedExpense.total_amount, selectedExpense.currency_code)}</p></div>
                <div><label className="text-sm text-gray-500">Төлбөрийн хэлбэр</label><p className="font-medium">{selectedExpense.payment_method === 'cash' ? 'Бэлэн' : 'Шилжүүлэг'}</p></div>
                <div className="col-span-2"><label className="text-sm text-gray-500">Тайлбар</label><p className="font-medium">{selectedExpense.notes || '-'}</p></div>
                <div><label className="text-sm text-gray-500">Бүртгэсэн</label><p className="font-medium">{selectedExpense.recorded_by_name || '-'}</p></div>
                <div><label className="text-sm text-gray-500">Бүртгэсэн огноо</label><p className="font-medium">{formatDateTime(selectedExpense.created_at)}</p></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentManagement;